import { TAB_COMPONENT_LOADERS, type TabHost, isTabKey } from '@/models/tab-registry';
import {
  type LayoutNodeSnapshot,
  TAB_STORAGE_KEY,
  type TabItemSnapshot,
  clearStoredSnapshot,
  parseSnapshot,
  toSnapshot,
} from '@/models/tab-snapshot';
import { TabService } from '@/services/tab.service';
import { AuthenticationStore } from '@/state/authentication.store';
import { type LayoutNode, type LayoutRoot, type Tab, TabStore, resolveActiveGroupId } from '@/state/tab.store';
import { Injectable, effect, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';

/** Inactive tabs stay mounted, so every restored tab costs a component. The oldest go first. */
const MAX_RESTORED_TABS = 20;

/** Better to lose the layout than to fill the session quota with one runaway payload. */
const MAX_SNAPSHOT_CHARS = 256_000;

/** A divider drag rewrites the sizes on every pointermove; only the size it ends on is stored. */
export const SIZES_WRITE_DELAY_MS = 250;

/**
 * Nothing renders until every tab has loaded, so a stalled chunk would hold the app on a blank page.
 * Generous, because a tab dropped here does not come back: the next write prunes it.
 */
export const TAB_LOAD_TIMEOUT_MS = 10_000;

/**
 * Keeps the tab layout alive across a page refresh.
 *
 * Not `withStorageSync` as elsewhere: rebuilding a tab needs an `Injector` and an awaited component
 * load, and its synchronous `parse` can do neither.
 */
@Injectable({ providedIn: 'root' })
export class TabPersistenceService {
  private readonly store = inject(TabStore);
  private readonly tabs = inject(TabService);
  private readonly authentication = inject(AuthenticationStore);

  private readonly hydrated = signal(false);
  private writeDisabled = false;

  /** Tells a change to the tree apart from one to the pane sizes. */
  private lastWrittenRoot: LayoutRoot | null = null;
  private pendingWrite: ReturnType<typeof setTimeout> | null = null;

  /** Mirrors the layout back to storage whenever the tree or a split's sizes change. */
  private readonly persistEffect = effect(() => {
    const root = this.store.root();
    const sizes = this.store.sizes();

    // Both reads come before the guard, so the effect stays subscribed while hydration is pending,
    // and `hydrated` is a signal so it re-runs the moment hydration ends.
    if (!this.hydrated()) {
      return;
    }

    // A change to the tree is one user action and is written at once; the sizes change once per
    // frame of a drag, so those are coalesced.
    const treeChanged = root !== this.lastWrittenRoot;
    this.lastWrittenRoot = root;

    if (treeChanged) {
      this.cancelPendingWrite();
      this.write(root, sizes);
      return;
    }

    this.cancelPendingWrite();
    this.pendingWrite = setTimeout(() => {
      this.pendingWrite = null;
      this.write(root, sizes);
    }, SIZES_WRITE_DELAY_MS);
  });

  private cancelPendingWrite(): void {
    if (this.pendingWrite !== null) {
      clearTimeout(this.pendingWrite);
      this.pendingWrite = null;
    }
  }

  /**
   * Rebuilds the layout from storage before the first render - see `TabStore.restore()` for why that
   * timing matters. Always resolves, and every path arms the writer: one bad boot must not stop the
   * rest of the session being persisted.
   */
  async hydrate(): Promise<void> {
    try {
      // The auth store reads its tokens synchronously on injection, so this is already accurate on
      // the login page, where the initializer also runs.
      if (!environment.storeTabState || !this.authentication.isLoggedIn()) {
        clearStoredSnapshot();
        return;
      }

      const snapshot = parseSnapshot(this.read());
      if (!snapshot?.root.layout) {
        clearStoredSnapshot();
        return;
      }

      const allowed = this.tabIdsWithinCap(snapshot.root.layout);
      const materialised = await this.materialiseNode(snapshot.root.layout, allowed);

      // A tab can go missing - a component dropped in a newer deploy. Pruning what it leaves behind
      // keeps the layout from rendering with no active tab.
      const layout = this.store.cleanLayoutState(materialised);
      if (!layout) {
        clearStoredSnapshot();
        return;
      }

      this.store.restore(
        { layout, activeGroupId: resolveActiveGroupId(layout, snapshot.root.activeGroupId) },
        this.resolveSizes(layout, snapshot.sizes),
      );
    } catch (error) {
      console.warn('Could not restore the tab layout', error);
      clearStoredSnapshot();
    } finally {
      this.hydrated.set(true);
    }
  }

  /**
   * Serialises the layout into session storage, or wipes the entry when there is nothing to keep.
   * Never throws: a failure disables further writes and leaves what is stored alone, so a refresh
   * restores the last layout that fit.
   */
  private write(root: LayoutRoot, sizes: Record<string, number[]>): void {
    if (this.writeDisabled) {
      return;
    }

    if (!environment.storeTabState || !root.layout) {
      clearStoredSnapshot();
      return;
    }

    try {
      const serialised = JSON.stringify(toSnapshot(root, sizes));

      if (serialised.length > MAX_SNAPSHOT_CHARS) {
        console.warn('Tab layout is too large to persist; a refresh will restore the last one that fit');
        return;
      }

      sessionStorage.setItem(TAB_STORAGE_KEY, serialised);
    } catch (error) {
      // Quota, private mode, or a payload that will not stringify. None is worth retrying.
      console.warn('Could not persist the tab layout; a refresh will restore the last one stored', error);
      this.writeDisabled = true;
    }
  }

  /** The raw stored snapshot, or `null` when there is none - or when storage is unreadable. */
  private read(): string | null {
    try {
      return sessionStorage.getItem(TAB_STORAGE_KEY);
    } catch {
      // Safari in private mode throws on access, not just on write.
      this.writeDisabled = true;
      return null;
    }
  }

  /**
   * Turns one stored node back into a live one, depth first. Anything that cannot be rebuilt is left
   * out, and a node with nothing left returns `null` for its parent to drop.
   */
  private async materialiseNode(node: LayoutNodeSnapshot, allowed: Set<string>): Promise<LayoutNode | null> {
    if (node.type === 'split') {
      const children = await Promise.all(node.children.map((child) => this.materialiseNode(child, allowed)));
      const kept = children.filter((child): child is LayoutNode => child !== null);

      // A split left with one child is collapsed later by cleanLayoutState.
      return kept.length ? { id: node.id, type: 'split', orientation: node.orientation, children: kept } : null;
    }

    // allSettled: a component that no longer exists must cost its own tab, not the group.
    const results = await Promise.allSettled(
      node.tabs.filter((item) => allowed.has(item.id)).map((item) => this.materialiseTab(item)),
    );
    const tabs = results.flatMap((result) => (result.status === 'fulfilled' && result.value ? [result.value] : []));

    // A dropped tab is invisible in the layout, so say why.
    for (const result of results) {
      if (result.status === 'rejected') {
        console.warn('Dropped a tab whose component could not be loaded', result.reason);
      }
    }

    if (!tabs.length) {
      return null;
    }

    return {
      id: node.id,
      type: 'group',
      // The stored active tab may be one of the ones that just went missing.
      activeTabId: tabs.some((tab) => tab.id === node.activeTabId) ? node.activeTabId : tabs[0].id,
      tabs,
    };
  }

  /**
   * `null` for a key that is no longer registered, a rejection when the component cannot be loaded
   * in time. `materialiseNode` absorbs both as one lost tab.
   */
  private async materialiseTab(item: TabItemSnapshot): Promise<Tab | null> {
    if (!isTabKey(item.componentKey)) {
      return null;
    }

    const loading = TAB_COMPONENT_LOADERS[item.componentKey]();
    const component = (await this.withLoadTimeout(loading, item.componentKey)) as TabHost;

    // The stored id is reused so activeTabId keeps naming something real.
    return this.tabs.createTab(component, item.title, item.icon, item.data, item.id);
  }

  /** The load, or a rejection once `TAB_LOAD_TIMEOUT_MS` has passed. Clears its timer either way. */
  private withLoadTimeout<T>(loading: Promise<T>, key: string): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error(`Loading '${key}' took longer than ${TAB_LOAD_TIMEOUT_MS}ms`)),
        TAB_LOAD_TIMEOUT_MS,
      );
    });

    return Promise.race([loading, deadline]).finally(() => clearTimeout(timer));
  }

  /**
   * The last `MAX_RESTORED_TABS` ids in layout order. Tabs are appended to their group, so the tail
   * is what was opened most recently.
   */
  private tabIdsWithinCap(node: LayoutNodeSnapshot): Set<string> {
    const ids: string[] = [];

    const collect = (current: LayoutNodeSnapshot): void => {
      if (current.type === 'group') {
        ids.push(...current.tabs.map((tab) => tab.id));
        return;
      }
      for (const child of current.children) {
        collect(child);
      }
    };

    collect(node);

    if (ids.length > MAX_RESTORED_TABS) {
      console.warn(`Restoring the most recent ${MAX_RESTORED_TABS} of ${ids.length} tabs`);
    }

    return new Set(ids.slice(-MAX_RESTORED_TABS));
  }

  /**
   * `--size: 0` is the CSS fallback, so a split that comes back without an entry renders zero-width
   * panes. Seed anything missing or stale, and keep only the splits that survived pruning.
   */
  private resolveSizes(layout: LayoutNode, stored: Record<string, number[]>): Record<string, number[]> {
    const sizes: Record<string, number[]> = {};

    const walk = (node: LayoutNode): void => {
      if (node.type !== 'split') {
        return;
      }

      // Own-property read: an id from the snapshot must not pick up an inherited member.
      const existing = Object.hasOwn(stored, node.id) ? stored[node.id] : undefined;
      sizes[node.id] =
        existing?.length === node.children.length
          ? existing
          : Array(node.children.length).fill(1 / node.children.length);

      for (const child of node.children) {
        walk(child);
      }
    };

    walk(layout);

    return sizes;
  }
}
